/**
 * Whether 1Click would quote the token, set against whether anyone could have
 * filled it (blackbeard/observer#45).
 *
 * On NEAR Intents a price exists only when a solver chooses to quote, and
 * "No liquidity available" is all a user is told when none does. The chain
 * records trades, never refusals. So the observer asks — keyless, `dry`, once
 * a minute each way — and beside every answer reads two things it can see for
 * itself: what the pool would pay for the same trade, and what each solver held
 * of the token the trade would need, one solver at a time.
 *
 * The strip is an emphasis chart, not a categorical one: its story is one
 * state, "no quote while a solver held enough to fill it", and that state alone
 * takes the accent. Everything else is the quiet ink, told apart by height
 * rather than hue — a quote, a refusal nobody could have filled, a probe that
 * got no answer. `--mark-quiet` is validated against `--accent` in both themes
 * (index.css).
 *
 * It records and does not accuse. A refusal while inventory existed has
 * benign explanations — risk limits, inventory reserved for other flows, a
 * pause during an audit — and the page says so beside the figures.
 */

import { useEffect, useMemo, useState } from 'react'

import type { QuoteAvailability } from '../api/generated/QuoteAvailability'
import type { QuotePause } from '../api/generated/QuotePause'
import type { QuoteProbe } from '../api/generated/QuoteProbe'
import type { QuoteProbes } from '../api/generated/QuoteProbes'
import { RequestFailed, fetchQuoteProbes } from '../api/rest'
import { price as fmtPrice, tokens as fmtTokens, utc, utcDay, when, zone } from '../lib/format'
import { shortNear } from '../lib/near'
import { tokenLabel } from '../lib/tokens'

const SPANS: [number, string][] = [
  [6, '6 h'],
  [24, '24 h'],
  [72, '3 d'],
]

const REFRESH_MS = 60_000

/** Cells per strip, whatever the span: each is one bucket of minutes. */
const BUCKETS = 144

/** Strip height, in the SVG's units and in pixels. */
const STRIP = 16

/** What a bucket shows, most significant first. */
type State = 'withheld' | 'quoted' | 'unquoted' | 'failed' | 'none'

/** A probe that came back "no liquidity" while a solver held enough. */
function withheld(p: QuoteProbe): boolean {
  return p.outcome === 'no_liquidity' && (p.solvers_able ?? 0) > 0
}

function stateOf(p: QuoteProbe): State {
  if (withheld(p)) return 'withheld'
  if (p.outcome === 'quoted') return 'quoted'
  if (p.outcome === 'failed') return 'failed'
  return 'unquoted'
}

const RANK: Record<State, number> = { withheld: 4, quoted: 3, unquoted: 2, failed: 1, none: 0 }

const LEGEND: { state: State; label: string }[] = [
  { state: 'withheld', label: 'our request refused, while a solver held enough to fill it' },
  { state: 'quoted', label: 'quoted' },
  { state: 'unquoted', label: 'our request refused, and no solver held enough' },
  { state: 'failed', label: 'the probe got no answer' },
]

export function QuotePanel({ chain, symbol: chainSymbol }: { chain: string; symbol: string }) {
  const [hours, setHours] = useState(24)
  const [data, setData] = useState<QuoteProbes | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [readout, setReadout] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    const load = () =>
      fetchQuoteProbes(chain, hours, controller.signal)
        .then((d) => {
          setData(d)
          setError(null)
        })
        .catch((e: unknown) => {
          if (controller.signal.aborted) return
          setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
        })
    load()
    const id = window.setInterval(load, REFRESH_MS)
    return () => {
      window.clearInterval(id)
      controller.abort()
    }
  }, [chain, hours])

  const meta = (token: string) => data?.tokens.find((t) => t.token === token)
  const label = (token: string) => tokenLabel(token, meta(token)?.symbol)
  // The chain's own symbol when it has been read, else the token's on NEAR.
  const symbol = chainSymbol || (data ? label(data.asset) : '')
  const amount = (raw: string | null, token: string) => {
    const m = meta(token)
    return raw && m ? `${fmtTokens(raw, m.decimals)} ${label(token)}` : '—'
  }
  /** The token a probe's trade gives, and the one it would receive. */
  const sides = (p: { direction: string; counter: string }) =>
    p.direction === 'sell'
      ? { given: data?.asset ?? '', received: p.counter }
      : { given: p.counter, received: data?.asset ?? '' }

  const rows = useMemo(() => strips(data), [data])
  const incidents = useMemo(
    () => (data ? data.probes.filter(withheld).slice(-12).reverse() : []),
    [data],
  )

  const describe = (p: QuoteProbe) => {
    const { given, received } = sides(p)
    const trade = `${p.direction === 'sell' ? 'selling' : 'buying with'} ${amount(p.size, given)}`
    const pool = p.pool_out ? `; the pool would have paid ${amount(p.pool_out, received)}` : ''
    const best = p.best_solver
      ? `; ${shortNear(p.best_solver)} held ${amount(p.best_held, received)}`
      : ''
    const verdict =
      p.outcome === 'quoted'
        ? `quoted${p.markup_bps !== null ? `, ${(p.markup_bps / 100).toFixed(2)}% below the pool` : ''}`
        : p.outcome === 'failed'
          ? `no answer (${p.message ?? 'probe failed'})`
          : `“${p.message ?? p.outcome}”`
    return `${when(p.at)}, ${trade}: ${verdict}${pool}${best}`
  }

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <h2 className="panel-title">Does 1Click quote {symbol}?</h2>
        <span className="eyebrow">
          one request, asked every minute, against what could have filled it
        </span>
      </div>
      <p className="panel-note" style={{ borderTop: 0 }}>
        <strong>The request:</strong> an exact-input dry quote with no key, paying and receiving
        inside NEAR Intents, {symbol} against USDC on NEAR or on Ethereum. A refusal is a refusal of
        this request, not proof nobody was quoted: 1Click filled two {symbol} buys on 7 October, at
        14:18 and 14:57 UTC, while every probe of this request was refused. A buyer paying from
        another chain or with another token, or holding a partner key, asks a different question,
        and NEAR does not show which. The 1Click fills listed under each stretch are how to tell
        when that happens.
      </p>

      <div className="pager" style={{ justifyContent: 'flex-start' }}>
        {SPANS.map(([h, l]) => (
          <button
            key={h}
            className="segmented panel-button"
            aria-pressed={hours === h}
            onClick={() => setHours(h)}
          >
            {l}
          </button>
        ))}
      </div>

      {error && <p className="empty">{error}</p>}
      {!error && !data && <p className="empty">Reading the probe record…</p>}
      {data && !data.probing && <p className="empty">The quote probe is not configured here.</p>}
      {data && data.probing && data.probes.length === 0 && (
        <p className="empty">No probes yet in this window: the first runs on the next minute.</p>
      )}

      {data && rows.length > 0 && (
        <>
          <div className="quote-strips">
            {rows.map((row) => (
              <div key={`${row.direction}-${row.counter}`} className="quote-strip">
                <div className="quote-strip-label">
                  {row.direction === 'sell'
                    ? `Selling ${symbol} for ${label(row.counter)}`
                    : `Buying ${symbol} with ${label(row.counter)}`}
                  <span className="receipt-meta">
                    {' '}
                    · {amount(row.size, sides(row).given)} each minute
                  </span>
                </div>
                <svg
                  className="quote-cells"
                  viewBox={`0 0 ${BUCKETS * 6} ${STRIP}`}
                  preserveAspectRatio="none"
                  role="img"
                  aria-label={summaryText(row, data.availability)}
                  onMouseLeave={() => setReadout(null)}
                >
                  <line
                    className="quote-baseline"
                    x1={0}
                    x2={BUCKETS * 6}
                    y1={STRIP - 0.5}
                    y2={STRIP - 0.5}
                  />
                  {row.buckets.map((b, i) => (
                    <g
                      key={i}
                      onMouseEnter={() =>
                        setReadout(
                          b.last
                            ? `${b.count} probe${b.count === 1 ? '' : 's'} — latest ${describe(b.last)}`
                            : `${when(new Date(b.from).toISOString())}: no probe`,
                        )
                      }
                    >
                      {/* The hit area is the whole column, wider than any mark. */}
                      <rect x={i * 6} y={0} width={6} height={STRIP} fill="transparent" />
                      <Cell state={b.state} x={i * 6} />
                    </g>
                  ))}
                </svg>
              </div>
            ))}
          </div>
          <div className="quote-axis receipt-meta">
            <span>{rows[0] ? when(new Date(rows[0].from).toISOString()) : ''}</span>
            <span>times in {zone()}</span>
            <span>now</span>
          </div>
          <p className="quote-readout receipt-meta" aria-live="polite">
            {readout ?? 'Hover the strip for any moment’s probes.'}
          </p>
          <ul className="quote-legend">
            {LEGEND.map((l) => (
              <li key={l.state}>
                <svg width={10} height={16} aria-hidden="true">
                  <Cell state={l.state} x={2} height={16} />
                </svg>
                {l.label}
              </li>
            ))}
          </ul>

          <div className="scroll-x">
            <table className="board">
              <caption className="visually-hidden">
                Probe outcomes over the last {data.hours} hours, by direction and counter token
              </caption>
              <thead>
                <tr>
                  <th scope="col" className="left">
                    Trade
                  </th>
                  <th scope="col">Answered</th>
                  <th scope="col">Quoted</th>
                  <th scope="col">Refused</th>
                  <th scope="col">…while a solver could fill</th>
                  <th scope="col">No answer</th>
                  <th scope="col">Median markup</th>
                </tr>
              </thead>
              <tbody>
                {data.availability.map((a) => (
                  <tr key={`${a.direction}-${a.counter}`}>
                    <td className="left">
                      {a.direction === 'sell' ? 'sell' : 'buy'} {symbol} · {label(a.counter)}
                    </td>
                    <td className="numeral">{a.answered}</td>
                    <td className="numeral">{share(a.quoted, a.answered)}</td>
                    <td className="numeral">{share(a.no_liquidity, a.answered)}</td>
                    <td className="numeral">
                      <strong>{share(a.no_liquidity_while_able, a.answered)}</strong>
                    </td>
                    <td className="numeral">{a.failed}</td>
                    <td className="numeral">
                      {a.median_markup_bps === null
                        ? '—'
                        : `${(a.median_markup_bps / 100).toFixed(2)}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Asymmetry data={data} label={label} />

          {data.pauses.length > 0 && (
            <div className="scroll-x">
              <table className="board">
                <caption className="visually-hidden">
                  Stretches with no quote, newest first, with what could have filled them and the
                  pool&apos;s price around them
                </caption>
                <thead>
                  <tr>
                    <th scope="col" className="left">
                      Refused for
                    </th>
                    <th scope="col" className="left">
                      From
                    </th>
                    <th scope="col" className="left">
                      Until
                    </th>
                    <th scope="col">Length</th>
                    <th scope="col">A solver could fill</th>
                    <th scope="col">
                      Pool price ({data.tokens[1] ? label(data.tokens[1].token) : 'counter'})
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.pauses.slice(0, 12).map((p) => (
                    <PauseRows
                      key={`${p.direction}-${p.counter}-${p.start}`}
                      p={p}
                      label={label}
                      symbol={symbol}
                      decimals={meta(data.asset)?.decimals ?? 12}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {incidents.length > 0 && (
            <div className="scroll-x">
              <table className="board">
                <caption className="visually-hidden">
                  The latest probes answered “no liquidity” while a solver held enough
                </caption>
                <thead>
                  <tr>
                    <th scope="col" className="left">
                      When
                    </th>
                    <th scope="col" className="left">
                      Trade asked for
                    </th>
                    <th scope="col">Pool would pay</th>
                    <th scope="col" className="left">
                      Largest solver holding
                    </th>
                    <th scope="col">Solvers able</th>
                  </tr>
                </thead>
                <tbody>
                  {incidents.map((p) => {
                    const { given, received } = sides(p)
                    return (
                      <tr key={`${p.at}-${p.direction}-${p.counter}-${p.size}`}>
                        <td className="left numeral event-when" title={utc(p.at)}>
                          {when(p.at)}
                        </td>
                        <td className="left">
                          {p.direction} {amount(p.size, given)}
                        </td>
                        <td className="numeral">{amount(p.pool_out, received)}</td>
                        <td className="left" title={p.best_solver ?? undefined}>
                          {p.best_solver
                            ? `${shortNear(p.best_solver)}: ${amount(p.best_held, received)}`
                            : '—'}
                        </td>
                        <td className="numeral">{p.solvers_able ?? '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {data && data.days.length > 0 && (
        <div className="scroll-x">
          <table className="board">
            <caption className="visually-hidden">Probe outcomes per UTC day, last 30 days</caption>
            <thead>
              <tr>
                <th scope="col" className="left">
                  Day (UTC)
                </th>
                <th scope="col" className="left">
                  Trade
                </th>
                <th scope="col">Answered</th>
                <th scope="col">Quoted</th>
                <th scope="col">Refused while a solver could fill</th>
                <th scope="col">No answer</th>
              </tr>
            </thead>
            <tbody>
              {data.days.map((d) => (
                <tr key={`${d.day}-${d.direction}-${d.counter}`}>
                  <td className="left numeral">{utcDay(d.day, true)}</td>
                  <td className="left">
                    {d.direction} {symbol} · {label(d.counter)}
                  </td>
                  <td className="numeral">{d.answered}</td>
                  <td className="numeral">{share(d.quoted, d.answered)}</td>
                  <td className="numeral">{share(d.no_liquidity_while_able, d.answered)}</td>
                  <td className="numeral">{d.failed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="panel-note">
        A stretch begins with the first minute this request was refused, and ends when a quote comes
        back. A minute the probe got no answer for is counted inside it rather than ending it, but
        more than a quarter hour without answers ends it as unobserved: claiming it ran on unseen
        would claim something nobody saw. The pool&apos;s price around each stretch is the
        probe&apos;s own reading of the pool, not anyone&apos;s quote, and a price that moved after
        a stretch is not evidence of why the stretch happened.
      </p>

      <p className="panel-note">
        Each minute the observer asks 1Click for a dry quote — nothing is committed — selling and
        buying {symbol} at the smallest size, and every size each quarter hour, without a key and
        inside the rate limit 1Click publishes. At the same moment it reads, from its own NEAR
        nodes, what the Ref pool would pay for the same trade and what each known solver holds of
        the token the trade would need. A solver counts as able only if it alone held at least what
        the pool would pay: solvers do not pool inventory. “No liquidity available” while a solver
        was able says inventory existed and no quote was given; it does not say why. Risk limits,
        inventory reserved for other flows, or a pause during an audit would all look the same from
        here, and which solvers 1Click asked, and what they answered, is private to them. 1Click’s
        terms allow no API data to be kept beyond a day, so only these observations are kept: the
        quote itself becomes a markup against the pool and is then discarded. A probe that got no
        answer is shown as that, never as a refusal.
      </p>
    </section>
  )
}

function share(n: number, of: number): string {
  if (of === 0) return '—'
  return `${n} (${Math.round((n / of) * 100)}%)`
}

function summaryText(
  row: { direction: string; counter: string },
  availability: QuoteAvailability[],
): string {
  const a = availability.find((x) => x.direction === row.direction && x.counter === row.counter)
  if (!a) return 'no probes'
  return `${a.answered} answered: ${a.quoted} quoted, ${a.no_liquidity} no quote, of which ${a.no_liquidity_while_able} while a solver could fill, ${a.failed} unanswered`
}

/** One cell: height and fill say the state; only the one that matters is in the accent. */
function Cell({ state, x, height = STRIP }: { state: State; x: number; height?: number }) {
  // Thin marks with a wide surface gap: a day of refusals is a run of
  // hairlines, not a slab.
  const w = 2.5
  switch (state) {
    case 'withheld':
      return <rect className="quote-withheld" x={x} y={0} width={w} height={height} rx={1} />
    case 'quoted':
      return (
        <rect
          className="quote-quoted"
          x={x}
          y={height * 0.4}
          width={w}
          height={height * 0.6}
          rx={1}
        />
      )
    case 'unquoted':
      return (
        <rect
          className="quote-unquoted"
          x={x + 0.5}
          y={height * 0.7}
          width={w - 1}
          height={height * 0.3 - 0.5}
          rx={1}
        />
      )
    case 'failed':
      return <rect className="quote-failed" x={x} y={height - 2} width={w} height={2} />
    default:
      return null
  }
}

interface Bucket {
  from: number
  state: State
  count: number
  last: QuoteProbe | null
}

interface Strip {
  direction: string
  counter: string
  /** The every-minute size. */
  size: string
  from: number
  buckets: Bucket[]
}

/** One strip per direction and counter, from the every-minute (smallest) probes. */
function strips(data: QuoteProbes | null): Strip[] {
  if (!data) return []
  const end = Date.now()
  const start = end - data.hours * 3_600_000
  const width = (end - start) / BUCKETS
  const out: Strip[] = []
  for (const a of data.availability) {
    const own = data.probes.filter((p) => p.direction === a.direction && p.counter === a.counter)
    if (own.length === 0) continue
    // The every-minute size is the smallest one probed.
    const size = own.reduce((m, p) => (BigInt(p.size) < BigInt(m) ? p.size : m), own[0]!.size)
    const buckets: Bucket[] = Array.from({ length: BUCKETS }, (_, i) => ({
      from: start + i * width,
      state: 'none' as State,
      count: 0,
      last: null,
    }))
    for (const p of own) {
      if (p.size !== size) continue
      const i = Math.min(BUCKETS - 1, Math.floor((Date.parse(p.at) - start) / width))
      const b = buckets[i]
      if (!b) continue
      b.count += 1
      const s = stateOf(p)
      if (RANK[s] > RANK[b.state]) b.state = s
      if (!b.last || Date.parse(p.at) > Date.parse(b.last.at)) b.last = p
    }
    out.push({ direction: a.direction, counter: a.counter, size, from: start, buckets })
  }
  return out
}

/** How a stretch's length reads: to the quote that ended it, or to now. */
function span(p: QuotePause): string {
  const end = Date.parse(p.resumed_at ?? p.last)
  const minutes = Math.max(1, Math.round((end - Date.parse(p.start)) / 60_000))
  if (minutes < 120) return `${minutes} min`
  const h = Math.floor(minutes / 60)
  return `${h} h ${minutes % 60} min`
}

function PauseRows({
  p,
  label,
  symbol,
  decimals,
}: {
  p: QuotePause
  label: (token: string) => string
  symbol: string
  decimals: number
}) {
  const until =
    p.end === 'resumed' && p.resumed_at
      ? when(p.resumed_at)
      : p.end === 'ongoing'
        ? 'still unquoted'
        : `${when(p.last)}, then unobserved`
  const prices = [p.price_start, p.price_end, p.price_1h, p.price_4h]
    .map((v) => (v === null ? '—' : fmtPrice(v)))
    .join(' → ')
  return (
    <>
      <tr>
        <td className="left">
          {p.direction === 'sell' ? 'selling' : 'buying'} {symbol} · {label(p.counter)}
        </td>
        <td className="left numeral event-when" title={utc(p.start)}>
          {p.began_before ? 'by ' : ''}
          {when(p.start)}
        </td>
        <td
          className="left numeral event-when"
          title={p.resumed_at ? utc(p.resumed_at) : utc(p.last)}
        >
          {until}
        </td>
        <td className="numeral">
          {p.began_before ? '≥ ' : ''}
          {span(p)}
        </td>
        <td className="numeral">
          {p.solver_able} of {p.unquoted} min
          {p.unanswered > 0 && <span className="receipt-meta"> · {p.unanswered} unanswered</span>}
        </td>
        <td
          className="numeral"
          title="at the start → at the end → an hour later → four hours later"
        >
          {prices}
        </td>
      </tr>
      {p.context && (
        <tr className="pause-context">
          <td colSpan={6} className="left receipt-meta">
            {contextText(p, symbol, decimals)}
          </td>
        </tr>
      )}
    </>
  )
}

/**
 * What happened on-chain during a pause, in a sentence. Counts and amounts
 * only: none of it says why the quotes stopped.
 */
function contextText(p: QuotePause, symbol: string, decimals: number): string {
  const c = p.context
  if (!c) return ''
  const q = (raw: string) => `${fmtTokens(raw, decimals)} ${symbol}`
  const parts: string[] = []
  parts.push(
    c.solver_swaps > 0
      ? `solvers swapped in the pool ${c.solver_swaps} times (sold ${q(c.solver_sold)}, bought ${q(c.solver_bought)})`
      : 'no solver swapped in the pool',
  )
  if (c.other_swaps > 0)
    parts.push(
      `others swapped with the pool directly ${c.other_swaps} times (sold ${q(c.other_sold)}, bought ${q(c.other_bought)})`,
    )
  parts.push(c.fills > 0 ? `${c.fills} fills through 1Click` : 'no fills through 1Click')
  if (c.minted_count > 0)
    parts.push(`${c.minted_count} bridge deposits brought ${q(c.minted)} to NEAR`)
  if (c.holder_start && c.held_start && c.held_end)
    parts.push(
      `the largest ${symbol} holder among solvers, ${shortNear(c.holder_start)}, held ${q(c.held_start)}${
        c.holder_end === c.holder_start ? ` → ${q(c.held_end)}` : ''
      }`,
    )
  for (const ch of c.pool_changes) {
    const bits = [
      ch.state_from && ch.state_to ? `state ${ch.state_from} → ${ch.state_to}` : null,
      ch.whitelisted.length ? `whitelisted ${ch.whitelisted.map(shortNear).join(', ')}` : null,
      ch.delisted.length ? `removed from whitelist ${ch.delisted.map(shortNear).join(', ')}` : null,
    ].filter(Boolean)
    if (bits.length) parts.push(`pool ${when(ch.at)}: ${bits.join('; ')}`)
  }
  return `During it: ${parts.join('; ')}.`
}

/**
 * Buy availability against sell availability, per counter: the gap the
 * launch evening made plain, when buying was quoted and selling never was.
 */
function Asymmetry({ data, label }: { data: QuoteProbes; label: (token: string) => string }) {
  const counters = [...new Set(data.availability.map((a) => a.counter))]
  const rate = (a: QuoteAvailability | undefined) =>
    a && a.answered > 0 ? (a.quoted / a.answered) * 100 : null
  const lines = counters.flatMap((c) => {
    const buy = rate(data.availability.find((a) => a.counter === c && a.direction === 'buy'))
    const sell = rate(data.availability.find((a) => a.counter === c && a.direction === 'sell'))
    if (buy === null || sell === null) return []
    const gap = Math.round(buy - sell)
    return [
      `${label(c)}: buying quoted ${Math.round(buy)}% of answered probes, selling ${Math.round(sell)}% — ${
        gap === 0
          ? 'no gap'
          : `${gap > 0 ? '+' : '−'}${Math.abs(gap)} points ${gap > 0 ? 'towards buyers' : 'towards sellers'}`
      }`,
    ]
  })
  if (lines.length === 0) return null
  return (
    <p className="quote-readout receipt-meta">
      {lines.map((l) => (
        <span key={l} style={{ display: 'block' }}>
          {l}
        </span>
      ))}
    </p>
  )
}
